import { Injectable } from '@angular/core';

import { OptionStatus } from '@cds/betting-offer';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { Store } from '@ngrx/store';
import {
    ApiKey,
    BetBuilderPriceService,
    PriceRequest,
    Status,
} from 'packages/sports/web/app/src/betbuilder/services/sportcast/bet-builder-price.service';
import { catchError, filter, firstValueFrom, map, switchMap } from 'rxjs';

import { IBetslipRootState, IBetslipState } from '../../../base/store/state';
import { isLinear } from '../../../base/utils/linear-utils';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BetBuilderPickId, PickId } from '../../../core/picks/pick-id';
import BetslipPickActions from '../../picks/actions';
import OptionPickFactory from '../../picks/factory/option-pick.factory';
import { IBetslipTypeState } from '../../types/state';

@Injectable()
export default class BetBuilderPlaceBetService {
    private readonly logger: SportsRemoteLogger;

    constructor(
        private betBuilderPriceService: BetBuilderPriceService,
        private bettingOfferApi: BettingOfferApi,
        private store: Store<IBetslipRootState>,
        private optionPickFactory: OptionPickFactory,
        loggerFactory: LoggerFactory,
        private betslipConfig: BetslipConfig,
    ) {
        this.logger = loggerFactory.getLogger('BetBuilderPlaceBet');
    }

    async updateBetBuilderPicks(state: IBetslipState, atBetPlacement: boolean = false): Promise<void> {
        const isLinearBetslip = isLinear(state);

        const betBuilderTypeState = this.getPickState(state.types, isLinearBetslip);

        const entainUiBetBuilderPicks = state.picks.pickList.filter(
            (p) =>
                BetslipBetBuilderPick.isEntainUiBetBuilderPick(p) &&
                Object.values(betBuilderTypeState).length &&
                betBuilderTypeState[p.id.toString()].isSelected,
        ) as BetslipBetBuilderPick[];

        if (!entainUiBetBuilderPicks.length) {
            return;
        }

        const result = entainUiBetBuilderPicks.map((existingPick) => {
            const stake = this.getStake(state.types, existingPick.id, isLinearBetslip) ?? 0;
            const apiKey = existingPick.useV2Key ? ApiKey.Sportcast : ApiKey.BetGenius;

            const priceRequest: PriceRequest = {
                bets: existingPick.sportcastOptions.map((sportcastOption) => sportcastOption.longId),
                externalFixtureId: existingPick.sportcastId!,
                sportId: existingPick.sportId,
                stake,
                saveBetslip: true,
                key: apiKey,
            };

            const updateObservable = this.betBuilderPriceService.requestPrice(priceRequest).pipe(
                filter((response) => {
                    if (response.betSlipUid != null && response.statusCode === Status.OK) {
                        return true;
                    }

                    const errorMessage =
                        'betBuilderPriceService requestPrice response not OK. BetSlipUid: ' +
                        response.betSlipUid +
                        ' StatusCode: ' +
                        response.statusCode;
                    throw new Error(errorMessage);
                }),
                switchMap((priceResponse) => {
                    return this.betBuilderPriceService.getBetFeedback(priceResponse.betSlipUid, existingPick.betgeniusId!, apiKey);
                }),
                switchMap((betFeedbackResponse) => {
                    return this.bettingOfferApi
                        .getPicks({
                            tv1Picks: [],
                            tv2Picks: [
                                {
                                    fixtureId: existingPick.fixture.fixtureId,
                                    optionMarketId: betFeedbackResponse.marketId,
                                    isClassicBetBuilder: true,
                                },
                            ],
                        })
                        .pipe(
                            map((pickResponse) => {
                                const market = pickResponse?.fixtures[0].optionMarkets.find((om) => om.id === betFeedbackResponse.marketId);
                                const option = market?.options?.length ? market.options[0] : null;
                                if (!option || option.status !== OptionStatus.Visible) {
                                    const errorMessage = 'created bet builder market not available: ' + betFeedbackResponse.marketId;
                                    throw new Error(errorMessage);
                                }

                                const existingPickId = existingPick.id;
                                const existingOdds = existingPick.option.prices[0].nativeOdds;
                                const existingComboPrevention = existingPick.market.comboPrevention;

                                existingPick.fixture = this.optionPickFactory.mapFixture(pickResponse?.fixtures[0]!);
                                this.optionPickFactory.mapFromOptions(existingPick, market!, option);
                                const newPriceId = existingPick.option.prices[0].id;
                                const pickId = new BetBuilderPickId(
                                    existingPick.eventId,
                                    existingPick.fixture.fixtureId,
                                    market?.id!,
                                    option.id,
                                    newPriceId,
                                );
                                existingPick.id = pickId;

                                // keep user selected odds
                                existingPick.option.prices[0].nativeOdds = existingOdds;

                                //keep market combo prevention
                                existingPick.market.comboPrevention = existingComboPrevention;

                                this.store.dispatch(
                                    BetslipPickActions.replacePick({ pickId: existingPickId, newPick: existingPick, atBetPlacement }),
                                );
                            }),
                        );
                }),
                catchError((err) => {
                    const errorMessage = 'Error while preparing betbuilder bet placement';
                    this.logger.error(err, errorMessage);
                    throw new Error(errorMessage);
                }),
            );

            return firstValueFrom(updateObservable);
        });

        await Promise.all(result);
    }

    private getPickState(types: IBetslipTypeState, isLinearBetslip: boolean): { [pickId: string]: { isSelected: boolean } } {
        if (isLinearBetslip) {
            return types.betBuilder.picks;
        }

        return this.betslipConfig.isSportcastAsComboEnabled ? types.comboBet.picks : types.singleBet.picks;
    }

    private getStake(types: IBetslipTypeState, pickId: PickId, isLinearBetslip: boolean): number | null {
        const rawPickId = pickId.toString();

        if (isLinearBetslip) {
            return types.betBuilder.picks[rawPickId].stake;
        }

        return this.betslipConfig.isSportcastAsComboEnabled ? types.comboBet.stake : types.singleBet.picks[rawPickId].stake;
    }
}
